<template>
  <div
    class="grid rounded-2xl bg-white shadow-xs mx-auto select-none"
    :class="[
      isHero
        ? 'gap-0.5 sm:gap-1 border border-border/70 p-1 sm:p-1.5 rounded-xl shadow-2xs'
        : 'gap-1.5 border-2 border-border p-3 shadow-md'
    ]"
    :style="{
      gridTemplateColumns: `repeat(${level.gridSize.cols}, ${cellSize})`,
      gridAutoRows: cellSize,
    }"
  >
    <template v-for="n in totalCells" :key="n - 1">
      <div v-if="!answer.has(cellKey(n - 1))" />
      <button
        v-else
        type="button"
        @click="$emit('cell-click', cellKey(n - 1))"
        class="flex items-center justify-center font-bold select-none cursor-pointer transition-all duration-150"
        :class="[
          isHero ? 'rounded-md sm:rounded-lg border' : 'rounded-xl border-2',
          getCellClass(cellKey(n - 1)),
          focus === cellKey(n - 1) && !locked.has(cellKey(n - 1)) ? 'border-primary ring-2 ring-primary/30 scale-105 z-10' : '',
          shakeCells.has(cellKey(n - 1)) ? 'shake-cell border-destructive bg-destructive/10' : '',
          glowCells.has(cellKey(n - 1)) ? 'glow-cell' : '',
        ]"
        :style="{ fontSize: `calc(${cellSize} * 0.58)` }"
      >
        <span
          v-if="getChar(cellKey(n - 1))"
          class="animate-pop inline-block"
        >
          {{ getChar(cellKey(n - 1)) }}
        </span>
      </button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { LevelItem, Fill } from '../lib/types'

const props = withDefaults(
  defineProps<{
    level: LevelItem
    fills: Record<string, Fill>
    locked: Set<string>
    focus: string | null
    shakeCells: Set<string>
    glowCells: Set<string>
    answer: Map<string, string>
    blankSet: Set<string>
    isHero?: boolean
  }>(),
  {
    isHero: false,
  }
)

defineEmits<{
  (e: 'cell-click', key: string): void
}>()

const totalCells = computed(() => props.level.gridSize.rows * props.level.gridSize.cols)

const cellSize = computed(() => {
  const maxDim = Math.max(props.level.gridSize.rows, props.level.gridSize.cols)
  if (props.isHero) {
    if (maxDim <= 5) return 'min(8.6vw, 2.15rem)'
    if (maxDim <= 7) return 'min(7.2vw, 1.8rem)'
    return 'min(6.2vw, 1.55rem)'
  }
  if (maxDim <= 5) return 'min(14.5vw, 4.2rem)'
  if (maxDim <= 7) return 'min(11vw, 3.5rem)'
  return 'min(9.5vw, 3rem)'
})

function cellKey(n: number): string {
  const r = Math.floor(n / props.level.gridSize.cols)
  const c = n % props.level.gridSize.cols
  return `${r},${c}`
}

function getChar(key: string): string | undefined {
  if (props.blankSet.has(key)) {
    return props.fills[key]?.ch
  }
  return props.answer.get(key)
}

function getCellClass(key: string): string {
  const isBlank = props.blankSet.has(key)
  const isLocked = props.locked.has(key)

  if (isLocked) {
    return 'bg-emerald-50 text-emerald-800 border-emerald-400 font-extrabold shadow-sm'
  }
  if (isBlank) {
    if (props.fills[key]) {
      return 'bg-secondary/80 text-primary border-primary font-bold shadow-sm'
    }
    return 'bg-secondary/30 text-muted border-dashed border-border hover:border-primary/60'
  }
  return 'bg-gray-50 text-clay border-border/80 font-bold'
}
</script>
